import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InputControlArea from '@src/components/common/InputControlArea';
import PdfPreview from '@src/components/common/PDFPreview';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import { memo, type ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

type DesktopViewProps = {
  preview: boolean;
  isScrolling?: boolean;
  isInputActive?: boolean;
  onPdfPageChange: (page: number) => void;
  onPdfPageTotalChange: (total: number) => void;
  pdfPage?: number;
};

/**
 * デスクトップビューコンポーネント - より柔軟なレスポンシブレイアウト
 */
export const DesktopView = memo(
  ({
    preview,
    isScrolling = false,
    isInputActive = false,
    pdfPage: _pdfPage = 1,
    onPdfPageChange: _onPdfPageChange,
    onPdfPageTotalChange: _onPdfPageTotalChange,
  }: DesktopViewProps): ReactElement => {
    const _isFocusMode = useSelector(selectFocusMode) || false;
    const location = useLocation();
    const isRirekishoMode = location.pathname.startsWith('/rirekisho');

    return preview ? (
      <Grid
        container
        direction="row"
        spacing={1} // スペーシングを削減してPDFエリアを最大化
        sx={{
          padding: { xs: 0.5, sm: 1, md: 1.5 },
          boxSizing: 'border-box',
          width: '100%',
          maxWidth: '100%',
          justifyContent: 'flex-start',
          height: '100%', // 高さを100%に設定
        }}
      >
        {/* 入力エリア - フォーカスモード時は6:6の比率で50%（中央配置）、通常時は5:7の比率で42%（約4:6） */}
        <Grid
          size={
            _isFocusMode
              ? {
                  xl: 6, // 50% (6/12) フォーカスモード時 - 中央配置
                  lg: 6, // 50% (6/12) フォーカスモード時 - 中央配置
                  md: 6, // 50% (6/12) フォーカスモード時 - 中央配置
                  sm: 12, // タブレット縦向き以下では全幅
                  xs: 12, // モバイルでは全幅
                }
              : {
                  xl: 5, // 42% (5/12) 通常時 - 約4:6の比率
                  lg: 5, // 42% (5/12) 通常時 - 約4:6の比率
                  md: 5, // 42% (5/12) 通常時 - デスクトップビューの最小サイズ
                  sm: 12, // タブレット縦向き以下では全幅
                  xs: 12, // モバイルでは全幅
                }
          }
          sx={{
            minWidth: _isFocusMode
              ? {
                  xl: '500px', // フォーカスモード時は50%に対応した最小幅
                  lg: '480px',
                  md: '450px',
                  sm: '100%', // タブレット以下では100%
                  xs: '100%', // モバイルでは100%
                }
              : {
                  xl: '420px', // 通常時の最小幅（42%に対応）
                  lg: '400px',
                  md: '360px',
                  sm: '100%', // タブレット以下では100%
                  xs: '100%', // モバイルでは100%
                }, // 最小幅を確保
            // フォーム専用の幅制御
            '& > div': {
              maxWidth: '100%',
              width: '100%',
            },
          }}
        >
          <Box
            data-scroll-container="true"
            sx={{
              maxHeight: {
                xs: '50vh', // モバイルでは画面の50%
                sm: '50vh', // タブレットでは画面の50%
                md: 'calc(100vh - 120px)', // デスクトップでは従来通り
              },
              overflowY: {
                xs: 'hidden', // モバイルでは縦スクロール無効
                sm: 'hidden', // タブレットでは縦スクロール無効
                md: 'auto', // デスクトップでは従来通り
              },
              position: 'relative', // InputControlAreaの配置用
              paddingTop: { lg: 2 }, // 上部に余白を確保してPaperの影が切れないように
              paddingLeft: { lg: 2 }, // 左側も同様に余白を確保
              paddingRight: { lg: 2, xl: 2.5 }, // 右側は画面幅に応じて余白を調整
              paddingBottom: { lg: 12 }, // 操作エリア分の余白を広めに確保（12 * 8px = 96px）
              scrollbarGutter: 'stable', // スクロールバー表示時のレイアウト揺れを防止
            }}
          >
            <Outlet />
            {/* 入力操作エリア */}
            <InputControlArea
              isScrolling={isScrolling}
              isInputActive={isInputActive}
              contentType={isRirekishoMode ? 'rirekisho' : 'resume'}
            />
          </Box>
        </Grid>
        {/* プレビューエリア - フォーカスモード時は6:6の比率で50%、通常時は5:7の比率で58%（約4:6） */}
        <Grid
          size={
            _isFocusMode
              ? {
                  xl: 6, // 50% (6/12) フォーカスモード時 - 中央配置
                  lg: 6, // 50% (6/12) フォーカスモード時 - 中央配置
                  md: 6, // 50% (6/12) フォーカスモード時 - 中央配置
                  sm: 12, // タブレット縦向き以下では全幅
                  xs: 12, // モバイルでは全幅
                }
              : {
                  xl: 7, // 58% (7/12) 通常時 - 約4:6の比率
                  lg: 7, // 58% (7/12) 通常時 - 約4:6の比率
                  md: 7, // 58% (7/12) 通常時 - デスクトップビューの最小サイズ
                  sm: 12, // タブレット縦向き以下では全幅
                  xs: 12, // モバイルでは全幅
                }
          }
          sx={{
            display: 'block', // 常に表示（レスポンシブはsizeプロパティで制御）
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <Box
            sx={{
              height: {
                xs: '50vh', // モバイルでは画面の50%
                sm: '50vh', // タブレットでは画面の50%
                md: 'calc(100vh - 80px)', // デスクトップでは従来通り
              },
              overflow: 'hidden',
              boxSizing: 'border-box',
              width: '100%',
              maxWidth: '100%',
              margin: 0, // マージンを削除
              padding: 0, // パディングを削除
              // PDF表示エリアを画面いっぱいに最適化
              '& > *': {
                width: '100%',
                height: '100%',
                margin: 0,
                padding: 0,
              },
            }}
          >
            <PdfPreview isScrolling={isScrolling} />
          </Box>
        </Grid>
      </Grid>
    ) : (
      <Box
        sx={{
          padding: { xs: 2, sm: 3, md: 4, lg: 6 },
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
        }}
      >
        <Outlet />
      </Box>
    );
  }
);

DesktopView.displayName = 'DesktopView';
